import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from '../context/I18nContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Users, Settings, BarChart3, Trash2, Shield, ShieldOff, Save, Loader2, Key, Check, AlertCircle, History, Search, Filter, ArrowLeft, ArrowRight, Copy, Mail, Send, Tag, Pencil, X, Upload, Dumbbell, Sparkles, Video } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EX_PAGE_SIZE = 24;
// Selectable OpenAI chat models (support JSON response_format)
const AI_MODEL_OPTIONS = ['gpt-4o-mini', 'gpt-4o', 'gpt-4.1-mini', 'gpt-4.1', 'gpt-4-turbo'];

// Split "1. ... 2. ... 3. ..." into one line per numbered step
function NumberedText({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(?=\d+\.\s)/).map(s => s.trim()).filter(Boolean);
  return (
    <div className="space-y-1">
      {parts.map((p, i) => <p key={i} className="text-sm text-stone font-inter">{p}</p>)}
    </div>
  );
}

export default function AdminPage() {
  const { t, language } = useI18n();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cookiesFileExists, setCookiesFileExists] = useState(false);
  const [uploadingCookies, setUploadingCookies] = useState(false);
  const cookiesInputRef = useRef(null);

  // API Keys state
  const [openaiKey, setOpenaiKey] = useState('');
  const [stripeKey, setStripeKey] = useState('');
  const [pexelsKey, setPexelsKey] = useState('');
  const [gmailAddress, setGmailAddress] = useState('');
  const [gmailAppPassword, setGmailAppPassword] = useState('');
  const [savingKey, setSavingKey] = useState(null);

  // SMTP state
  const [smtpProvider, setSmtpProvider] = useState('custom');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('');
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpFromEmail, setSmtpFromEmail] = useState('');
  const [smtpFromName, setSmtpFromName] = useState('MealPlanner');
  const [smtpUseSsl, setSmtpUseSsl] = useState(true);
  const [testingEmail, setTestingEmail] = useState(false);

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditSkip, setAuditSkip] = useState(0);
  const [auditLimit] = useState(50);
  const [auditAction, setAuditAction] = useState('');
  const [auditQuery, setAuditQuery] = useState('');
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Log details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState({});
  const [savingTemplateId, setSavingTemplateId] = useState(null);
  const [testingTemplateId, setTestingTemplateId] = useState(null);
  const [activeTemplateLang, setActiveTemplateLang] = useState({});
  const [templateEdits, setTemplateEdits] = useState({});

  // Stripe price IDs local state (saved on blur, not on every keystroke)
  const [priceProMonthly, setPriceProMonthly] = useState('');
  const [priceProYearly, setPriceProYearly] = useState('');
  const [pricePremiumMonthly, setPricePremiumMonthly] = useState('');
  const [pricePremiumYearly, setPricePremiumYearly] = useState('');

  // Food categories state
  const [foodCategories, setFoodCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [newCatEs, setNewCatEs] = useState('');
  const [newCatEn, setNewCatEn] = useState('');
  const [savingCat, setSavingCat] = useState(false);
  const [editingCat, setEditingCat] = useState(null); // { category_id, name_es, name_en }
  const [deletingCatId, setDeletingCatId] = useState(null);

  // Exercise library state (Phase 3)
  const [exercises, setExercises] = useState([]);
  const [loadingExercises, setLoadingExercises] = useState(false);
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [exerciseTotal, setExerciseTotal] = useState(0);
  const [importLimit, setImportLimit] = useState(20);
  const [importBodyPart, setImportBodyPart] = useState('');
  const [importingExercises, setImportingExercises] = useState(false);
  const [exercisePage, setExercisePage] = useState(0);
  const [selectedExercise, setSelectedExercise] = useState(null);

  const fetchExercises = async (page = exercisePage) => {
    setLoadingExercises(true);
    try {
      const params = new URLSearchParams();
      if (exerciseSearch) params.set('search', exerciseSearch);
      params.set('limit', EX_PAGE_SIZE);
      params.set('offset', page * EX_PAGE_SIZE);
      const res = await fetch(`${API}/exercises?${params.toString()}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setExercises(data.exercises || []);
        setExerciseTotal(data.total || 0);
        setExercisePage(page);
      }
    } catch (e) { console.error('Error fetching exercises:', e); }
    finally { setLoadingExercises(false); }
  };

  const searchExercises = () => fetchExercises(0);

  const handleGenerateBase = async () => {
    setImportingExercises(true);
    try {
      const res = await fetch(`${API}/admin/exercises/generate-base`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ limit: parseInt(importLimit) || 20, body_part: importBodyPart || null }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.no_more) {
          toast.info(language === 'es' ? 'Ya has importado todos los ejercicios disponibles.' : 'You have imported all available exercises.');
        } else {
          const rem = data.remaining ? (language === 'es' ? ` · quedan ${data.remaining} por importar` : ` · ${data.remaining} left`) : '';
          toast.success((language === 'es'
            ? `Importados ${data.created}, fallidos ${data.failed}`
            : `Imported ${data.created}, failed ${data.failed}`) + rem);
        }
        fetchExercises();
      } else {
        toast.error(data.detail || t('errors.generic'));
      }
    } catch { toast.error(t('errors.generic')); }
    finally { setImportingExercises(false); }
  };

  const handleDeleteExercise = async (id) => {
    try {
      const res = await fetch(`${API}/admin/exercises/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { fetchExercises(exercisePage); }
      else { toast.error(t('errors.generic')); }
    } catch { toast.error(t('errors.generic')); }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchAuditLogs();
    }
    if (activeTab === 'categories') {
      fetchFoodCategories();
    }
    if (activeTab === 'emails') {
      fetchEmailTemplates();
    }
    if (activeTab === 'exercises') {
      fetchExercises();
    }
  }, [activeTab, auditSkip, auditAction]);

  // Populate Stripe price ID fields when config loads
  useEffect(() => {
    if (config) {
      setPriceProMonthly(config.stripe_price_pro_monthly || '');
      setPriceProYearly(config.stripe_price_pro_yearly || '');
      setPricePremiumMonthly(config.stripe_price_premium_monthly || '');
      setPricePremiumYearly(config.stripe_price_premium_yearly || '');
    }
  }, [config?.stripe_price_pro_monthly, config?.stripe_price_pro_yearly, config?.stripe_price_premium_monthly, config?.stripe_price_premium_yearly]);

  // Populate SMTP fields when config loads
  useEffect(() => {
    if (config) {
      if (config.smtp_host) setSmtpHost(config.smtp_host);
      if (config.smtp_port) setSmtpPort(String(config.smtp_port));
      if (config.smtp_from_email) setSmtpFromEmail(config.smtp_from_email);
      if (config.smtp_from_name) setSmtpFromName(config.smtp_from_name);
      if (config.smtp_use_ssl !== undefined) setSmtpUseSsl(config.smtp_use_ssl);
      // Auto-detect provider from host
      if (config.smtp_host === 'smtp.gmail.com') setSmtpProvider('gmail');
      else if (config.smtp_host === 'smtp.hostinger.com') setSmtpProvider('hostinger');
      else if (config.smtp_host) setSmtpProvider('custom');
    }
  }, [config]);

  const fetchAuditLogs = async () => {
    setLoadingLogs(true);
    try {
      let url = `${API}/admin/audit-logs?limit=${auditLimit}&skip=${auditSkip}`;
      if (auditAction) url += `&action=${auditAction}`;
      if (auditQuery) url += `&query=${encodeURIComponent(auditQuery)}`;

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs);
        setAuditTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleAuditSearch = (e) => {
    e.preventDefault();
    setAuditSkip(0);
    fetchAuditLogs();
  };

  const fetchEmailTemplates = async () => {
    try {
      const res = await fetch(`${API}/admin/email-templates`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEmailTemplates(data);
        const edits = {};
        const langs = {};
        Object.entries(data).forEach(([id, tpl]) => {
          edits[id] = { subject_es: tpl.subject_es, subject_en: tpl.subject_en, title_es: tpl.title_es, title_en: tpl.title_en, body_es: tpl.body_es, body_en: tpl.body_en };
          langs[id] = 'es';
        });
        setTemplateEdits(edits);
        setActiveTemplateLang(prev => ({ ...langs, ...prev }));
      }
    } catch (err) {
      console.error('Error fetching email templates:', err);
    }
  };

  const saveEmailTemplate = async (templateId) => {
    setSavingTemplateId(templateId);
    try {
      const res = await fetch(`${API}/admin/email-templates/${templateId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(templateEdits[templateId]),
      });
      if (res.ok) {
        const updated = await res.json();
        setEmailTemplates(prev => ({ ...prev, [templateId]: updated }));
        toast.success(language === 'es' ? 'Plantilla guardada' : 'Template saved');
      } else {
        toast.error(language === 'es' ? 'Error al guardar' : 'Error saving');
      }
    } catch (err) {
      toast.error(language === 'es' ? 'Error al guardar' : 'Error saving');
    } finally {
      setSavingTemplateId(null);
    }
  };

  const testEmailTemplate = async (templateId) => {
    setTestingTemplateId(templateId);
    try {
      const res = await fetch(`${API}/admin/email-templates/${templateId}/test`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
      } else {
        const err = await res.json();
        toast.error(err.detail || (language === 'es' ? 'Error al enviar test' : 'Error sending test'));
      }
    } catch (err) {
      toast.error(language === 'es' ? 'Error al enviar test' : 'Error sending test');
    } finally {
      setTestingTemplateId(null);
    }
  };

  const resetEmailTemplate = async (templateId) => {
    try {
      const res = await fetch(`${API}/admin/email-templates/${templateId}/reset`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        const def = data.template;
        setEmailTemplates(prev => ({ ...prev, [templateId]: { ...def, customized: false } }));
        setTemplateEdits(prev => ({ ...prev, [templateId]: { subject_es: def.subject_es, subject_en: def.subject_en, title_es: def.title_es, title_en: def.title_en, body_es: def.body_es, body_en: def.body_en } }));
        toast.success(language === 'es' ? 'Plantilla restaurada' : 'Template reset to default');
      } else {
        toast.error(language === 'es' ? 'Error al restaurar' : 'Error resetting');
      }
    } catch (err) {
      toast.error(language === 'es' ? 'Error al restaurar' : 'Error resetting');
    }
  };

  const fetchFoodCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await fetch(`${API}/food-categories`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setFoodCategories(data.categories);
      }
    } catch (err) {
      console.error('Error fetching food categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const createFoodCategory = async () => {
    if (!newCatEs.trim() || !newCatEn.trim()) return;
    setSavingCat(true);
    try {
      const res = await fetch(`${API}/food-categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name_es: newCatEs.trim(), name_en: newCatEn.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setFoodCategories(prev => [...prev, data.category].sort((a, b) => a.name_es.localeCompare(b.name_es)));
        setNewCatEs('');
        setNewCatEn('');
        toast.success(language === 'es' ? 'Categoría creada' : 'Category created');
      } else {
        toast.error(language === 'es' ? 'Error al crear categoría' : 'Error creating category');
      }
    } catch (err) {
      toast.error(language === 'es' ? 'Error al crear categoría' : 'Error creating category');
    } finally {
      setSavingCat(false);
    }
  };

  const updateFoodCategory = async () => {
    if (!editingCat || !editingCat.name_es.trim() || !editingCat.name_en.trim()) return;
    setSavingCat(true);
    try {
      const res = await fetch(`${API}/food-categories/${editingCat.category_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name_es: editingCat.name_es.trim(), name_en: editingCat.name_en.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setFoodCategories(prev =>
          prev.map(c => c.category_id === editingCat.category_id ? data.category : c)
              .sort((a, b) => a.name_es.localeCompare(b.name_es))
        );
        setEditingCat(null);
        toast.success(language === 'es' ? 'Categoría actualizada' : 'Category updated');
      } else {
        toast.error(language === 'es' ? 'Error al actualizar' : 'Error updating category');
      }
    } catch (err) {
      toast.error(language === 'es' ? 'Error al actualizar' : 'Error updating category');
    } finally {
      setSavingCat(false);
    }
  };

  const deleteFoodCategory = async (categoryId) => {
    setDeletingCatId(categoryId);
    try {
      const res = await fetch(`${API}/food-categories/${categoryId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setFoodCategories(prev => prev.filter(c => c.category_id !== categoryId));
        toast.success(language === 'es' ? 'Categoría eliminada' : 'Category deleted');
      } else {
        toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting category');
      }
    } catch (err) {
      toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting category');
    } finally {
      setDeletingCatId(null);
    }
  };

  const fetchData = async () => {
    try {
      const [usersRes, statsRes, configRes] = await Promise.all([
        fetch(`${API}/admin/users`, { credentials: 'include' }),
        fetch(`${API}/admin/stats`, { credentials: 'include' }),
        fetch(`${API}/admin/config`, { credentials: 'include' }),
      ]);

      if (usersRes.ok) setUsers(await usersRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (configRes.ok) setConfig(await configRes.json());

      const cookiesRes = await fetch(`${API}/admin/youtube-cookies/status`, { credentials: 'include' });
      if (cookiesRes.ok) { const d = await cookiesRes.json(); setCookiesFileExists(d.exists); }
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = async (key, value) => {
    try {
      const response = await fetch(`${API}/admin/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [key]: value }),
      });

      if (response.ok) {
        const updatedConfig = await response.json();
        setConfig(updatedConfig);
        toast.success(t('success.saved'));
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      console.error('Error updating config:', error);
      toast.error(t('errors.generic'));
    }
  };

  const saveApiKey = async (keyType) => {
    setSavingKey(keyType);
    let keyValue, keyField;

    if (keyType === 'openai') {
      keyValue = openaiKey;
      keyField = 'openai_api_key';
    } else if (keyType === 'stripe') {
      keyValue = stripeKey;
      keyField = 'stripe_api_key';
    } else if (keyType === 'pexels') {
      keyValue = pexelsKey;
      keyField = 'pexels_api_key';
    }

    try {
      const response = await fetch(`${API}/admin/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [keyField]: keyValue }),
      });

      if (response.ok) {
        const updatedConfig = await response.json();
        setConfig(updatedConfig);
        // Clear the input after successful save
        if (keyType === 'openai') setOpenaiKey('');
        else if (keyType === 'stripe') setStripeKey('');
        else if (keyType === 'pexels') setPexelsKey('');
        toast.success(language === 'es' ? 'API Key guardada correctamente' : 'API Key saved successfully');
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      console.error('Error saving API key:', error);
      toast.error(t('errors.generic'));
    } finally {
      setSavingKey(null);
    }
  };

  const handleSmtpProviderChange = (provider) => {
    setSmtpProvider(provider);
    if (provider === 'gmail') {
      setSmtpHost('smtp.gmail.com');
      setSmtpPort('465');
      setSmtpUseSsl(true);
    } else if (provider === 'hostinger') {
      setSmtpHost('smtp.hostinger.com');
      setSmtpPort('465');
      setSmtpUseSsl(true);
    }
    // 'custom' leaves fields as-is for manual editing
  };

  const saveSmtpConfig = async () => {
    if (!smtpHost || !smtpUser || !smtpPassword) {
      toast.error(language === 'es' ? 'Completa servidor, usuario y contraseña' : 'Fill in server, user and password');
      return;
    }

    setSavingKey('smtp');

    try {
      const response = await fetch(`${API}/admin/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          smtp_host: smtpHost,
          smtp_port: parseInt(smtpPort) || 465,
          smtp_user: smtpUser,
          smtp_password: smtpPassword,
          smtp_from_email: smtpFromEmail || smtpUser,
          smtp_from_name: smtpFromName || 'MealPlanner',
          smtp_use_ssl: smtpUseSsl,
        }),
      });

      if (response.ok) {
        const updatedConfig = await response.json();
        setConfig(updatedConfig);
        setSmtpPassword('');
        toast.success(language === 'es' ? 'Configuración SMTP guardada' : 'SMTP configuration saved');
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      console.error('Error saving SMTP config:', error);
      toast.error(t('errors.generic'));
    } finally {
      setSavingKey(null);
    }
  };

  const clearSmtpConfig = async () => {
    setSavingKey('smtp');
    try {
      const response = await fetch(`${API}/admin/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          smtp_host: '', smtp_user: '', smtp_password: '',
          smtp_from_email: '', gmail_address: '', gmail_app_password: '',
        }),
      });
      if (response.ok) {
        const updatedConfig = await response.json();
        setConfig(updatedConfig);
        setSmtpHost(''); setSmtpPort(''); setSmtpUser('');
        setSmtpPassword(''); setSmtpFromEmail('');
        toast.success(language === 'es' ? 'Configuración SMTP eliminada' : 'SMTP configuration cleared');
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setSavingKey(null);
    }
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      const response = await fetch(`${API}/admin/test-email`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      } else {
        const err = await response.json();
        toast.error(err.detail || t('errors.generic'));
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setTestingEmail(false);
    }
  };

  const handleUploadCookies = async (file) => {
    if (!file) return;
    setUploadingCookies(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API}/admin/youtube-cookies`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (res.ok) {
        setCookiesFileExists(true);
        toast.success(language === 'es' ? 'Cookies subidas correctamente' : 'Cookies uploaded successfully');
      } else {
        toast.error(language === 'es' ? 'Error al subir las cookies' : 'Error uploading cookies');
      }
    } catch {
      toast.error(language === 'es' ? 'Error al subir las cookies' : 'Error uploading cookies');
    } finally {
      setUploadingCookies(false);
    }
  };

  const handleDeleteCookies = async () => {
    try {
      await fetch(`${API}/admin/youtube-cookies`, { method: 'DELETE', credentials: 'include' });
      setCookiesFileExists(false);
      toast.success(language === 'es' ? 'Cookies eliminadas' : 'Cookies deleted');
    } catch {
      toast.error(language === 'es' ? 'Error al eliminar las cookies' : 'Error deleting cookies');
    }
  };

  const clearApiKey = async (keyType) => {
    setSavingKey(keyType);
    let updateData = {};

    if (keyType === 'openai') {
      updateData = { openai_api_key: '' };
    } else if (keyType === 'stripe') {
      updateData = { stripe_api_key: '' };
    } else if (keyType === 'pexels') {
      updateData = { pexels_api_key: '' };
    }

    try {
      const response = await fetch(`${API}/admin/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        const updatedConfig = await response.json();
        setConfig(updatedConfig);
        toast.success(language === 'es' ? 'API Key eliminada (se usará la del servidor)' : 'API Key cleared (will use server default)');
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      console.error('Error clearing API key:', error);
      toast.error(t('errors.generic'));
    } finally {
      setSavingKey(null);
    }
  };

  const toggleUserRole = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';

    try {
      const response = await fetch(`${API}/admin/users/${userId}/role?role=${newRole}`, {
        method: 'PUT',
        credentials: 'include',
      });

      if (response.ok) {
        setUsers(users.map(u =>
          u.user_id === userId ? { ...u, role: newRole } : u
        ));
        toast.success(t('success.saved'));
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      console.error('Error toggling role:', error);
      toast.error(t('errors.generic'));
    }
  };

  const updateUserPlan = async (userId, newPlan) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/plan?plan=${newPlan}`, {
        method: 'PUT',
        credentials: 'include',
      });

      if (response.ok) {
        setUsers(users.map(u =>
          u.user_id === userId ? { ...u, plan: newPlan } : u
        ));
        toast.success(t('success.saved'));
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      console.error('Error updating plan:', error);
      toast.error(t('errors.generic'));
    }
  };

  const deleteUser = async () => {
    if (!userToDelete) return;

    try {
      const response = await fetch(`${API}/admin/users/${userToDelete.user_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setUsers(users.filter(u => u.user_id !== userToDelete.user_id));
        toast.success(t('success.deleted'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(t('errors.generic'));
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <h1 className="font-manrope font-bold text-2xl md:text-3xl text-charcoal mb-6 md:mb-8">
          {t('admin.title')}
        </h1>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="overflow-x-auto pb-1 -mx-4 px-4 md:mx-0 md:px-0">
            <TabsList className="bg-muted/50 p-1 rounded-2xl flex-nowrap w-max min-w-full md:w-auto">
              <TabsTrigger
                value="users"
                className="rounded-xl px-3 md:px-6 py-2 md:py-3 font-manrope data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="admin-users-tab"
              >
                <Users className="w-4 h-4 md:mr-2" strokeWidth={1.5} />
                <span className="hidden sm:inline">{t('admin.users')}</span>
              </TabsTrigger>
              <TabsTrigger
                value="config"
                className="rounded-xl px-3 md:px-6 py-2 md:py-3 font-manrope data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="admin-config-tab"
              >
                <Settings className="w-4 h-4 md:mr-2" strokeWidth={1.5} />
                <span className="hidden sm:inline">{t('admin.config')}</span>
              </TabsTrigger>
              <TabsTrigger
                value="stats"
                className="rounded-xl px-3 md:px-6 py-2 md:py-3 font-manrope data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="admin-stats-tab"
              >
                <BarChart3 className="w-4 h-4 md:mr-2" strokeWidth={1.5} />
                <span className="hidden sm:inline">{t('admin.stats')}</span>
              </TabsTrigger>
              <TabsTrigger
                value="audit"
                className="rounded-xl px-3 md:px-6 py-2 md:py-3 font-manrope data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="admin-audit-tab"
              >
                <History className="w-4 h-4 md:mr-2" strokeWidth={1.5} />
                <span className="hidden sm:inline">{language === 'es' ? 'Auditoría' : 'Audit'}</span>
              </TabsTrigger>
              <TabsTrigger
                value="categories"
                className="rounded-xl px-3 md:px-6 py-2 md:py-3 font-manrope data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="admin-categories-tab"
              >
                <Tag className="w-4 h-4 md:mr-2" strokeWidth={1.5} />
                <span className="hidden sm:inline">{language === 'es' ? 'Categorías' : 'Categories'}</span>
              </TabsTrigger>
              <TabsTrigger
                value="emails"
                className="rounded-xl px-3 md:px-6 py-2 md:py-3 font-manrope data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="admin-emails-tab"
              >
                <Mail className="w-4 h-4 md:mr-2" strokeWidth={1.5} />
                <span className="hidden sm:inline">Emails</span>
              </TabsTrigger>
              <TabsTrigger
                value="exercises"
                className="rounded-xl px-3 md:px-6 py-2 md:py-3 font-manrope data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid="admin-exercises-tab"
              >
                <Dumbbell className="w-4 h-4 md:mr-2" strokeWidth={1.5} />
                <span className="hidden sm:inline">{language === 'es' ? 'Ejercicios' : 'Exercises'}</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-white rounded-3xl shadow-soft overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px]">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 md:p-4 font-manrope font-semibold text-charcoal text-sm">Usuario</th>
                      <th className="text-left p-3 md:p-4 font-manrope font-semibold text-charcoal text-sm">Plan</th>
                      <th className="text-left p-3 md:p-4 font-manrope font-semibold text-charcoal text-sm">Rol</th>
                      <th className="text-left p-3 md:p-4 font-manrope font-semibold text-charcoal text-sm">Recetas</th>
                      <th className="text-left p-3 md:p-4 font-manrope font-semibold text-charcoal text-sm">Menús</th>
                      <th className="text-left p-3 md:p-4 font-manrope font-semibold text-charcoal text-sm">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.user_id} className="border-t border-border" data-testid={`user-row-${user.user_id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            {user.picture ? (
                              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                            ) : (
                              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                <span className="font-manrope font-bold text-primary">
                                  {user.name?.[0]?.toUpperCase()}
                                </span>
                              </div>
                            )}
                            <div>
                              <p className="font-manrope font-medium text-charcoal">{user.name}</p>
                              <p className="font-inter text-sm text-stone">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <select
                            value={user.plan}
                            onChange={(e) => updateUserPlan(user.user_id, e.target.value)}
                            className={`px-2 py-1 rounded-lg text-xs font-manrope font-semibold outline-none border-none cursor-pointer ${user.plan === 'premium'
                              ? 'bg-primary/10 text-primary'
                              : user.plan === 'pro'
                                ? 'bg-sage/10 text-sage'
                                : 'bg-muted text-stone'
                              }`}
                          >
                            <option value="free">Free</option>
                            <option value="pro">Pro</option>
                            <option value="premium">Premium</option>
                          </select>
                        </td>
                        <td className="p-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-manrope font-semibold ${user.role === 'admin'
                            ? 'bg-sage/10 text-sage'
                            : 'bg-muted text-stone'
                            }`}>
                            {user.role === 'admin' ? 'Admin' : 'User'}
                          </span>
                        </td>
                        <td className="p-4 font-inter text-charcoal">{user.recipe_count || 0}</td>
                        <td className="p-4 font-inter text-charcoal">{user.menu_count || 0}</td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => toggleUserRole(user.user_id, user.role)}
                              className="text-stone hover:text-sage"
                              title={user.role === 'admin' ? t('admin.removeAdmin') : t('admin.makeAdmin')}
                              data-testid={`toggle-role-${user.user_id}`}
                            >
                              {user.role === 'admin' ? (
                                <ShieldOff className="w-4 h-4" strokeWidth={1.5} />
                              ) : (
                                <Shield className="w-4 h-4" strokeWidth={1.5} />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setUserToDelete(user);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-stone hover:text-destructive"
                              data-testid={`delete-user-${user.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Config Tab */}
          <TabsContent value="config">
            <div className="space-y-6">
              {/* API Keys Section */}
              <Card className="bg-white rounded-3xl shadow-soft p-6 space-y-6">
                <div className="flex items-center gap-2 mb-2">
                  <Key className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  <h3 className="font-manrope font-bold text-lg text-charcoal">
                    {language === 'es' ? 'Claves API' : 'API Keys'}
                  </h3>
                </div>
                <p className="font-inter text-sm text-stone -mt-4 mb-4">
                  {language === 'es'
                    ? 'Configura las claves API para los servicios externos. Estas claves sobrescriben las configuradas en el servidor.'
                    : 'Configure API keys for external services. These keys override server-configured ones.'}
                </p>

                {/* OpenAI API Key */}
                <div className="p-4 bg-muted/50 rounded-2xl space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-manrope font-semibold text-charcoal">OpenAI API Key</p>
                      <p className="font-inter text-sm text-stone">
                        {language === 'es' ? 'Para el cálculo de calorías con IA' : 'For AI calorie calculation'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {config?.openai_api_key_set ? (
                        <span className="flex items-center gap-1 text-sage text-sm font-inter">
                          <Check className="w-4 h-4" />
                          {language === 'es' ? 'Configurada' : 'Configured'}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-500 text-sm font-inter">
                          <AlertCircle className="w-4 h-4" />
                          {language === 'es' ? 'No configurada' : 'Not configured'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={openaiKey}
                      onChange={(e) => setOpenaiKey(e.target.value)}
                      placeholder={language === 'es' ? 'Introduce nueva API Key (sk-...)' : 'Enter new API Key (sk-...)'}
                      className="h-12 rounded-xl border-border bg-white font-inter flex-1"
                      data-testid="openai-api-key-input"
                    />
                    <Button
                      onClick={() => saveApiKey('openai')}
                      disabled={!openaiKey || savingKey === 'openai'}
                      className="h-12 rounded-xl bg-primary hover:bg-primary/90"
                      data-testid="save-openai-key-button"
                    >
                      {savingKey === 'openai' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                    </Button>
                    {config?.openai_api_key_set && (
                      <Button
                        variant="outline"
                        onClick={() => clearApiKey('openai')}
                        disabled={savingKey === 'openai'}
                        className="h-12 rounded-xl text-destructive hover:text-destructive"
                        data-testid="clear-openai-key-button"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {config?.openai_api_key_set && (
                    <p className="text-xs text-stone/70 font-inter">
                      {language === 'es'
                        ? 'Por seguridad, la clave actual no se muestra. Introduce una nueva para reemplazarla.'
                        : 'For security, the current key is not displayed. Enter a new one to replace it.'}
                    </p>
                  )}
                </div>

                {/* Stripe API Key */}
                <div className="p-4 bg-muted/50 rounded-2xl space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-manrope font-semibold text-charcoal">Stripe API Key</p>
                      <p className="font-inter text-sm text-stone">
                        {language === 'es' ? 'Para procesar pagos de suscripciones' : 'For processing subscription payments'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {config?.stripe_api_key_set ? (
                        <span className="flex items-center gap-1 text-sage text-sm font-inter">
                          <Check className="w-4 h-4" />
                          {language === 'es' ? 'Configurada' : 'Configured'}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-500 text-sm font-inter">
                          <AlertCircle className="w-4 h-4" />
                          {language === 'es' ? 'No configurada' : 'Not configured'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={stripeKey}
                      onChange={(e) => setStripeKey(e.target.value)}
                      placeholder={language === 'es' ? 'Introduce nueva API Key (sk_...)' : 'Enter new API Key (sk_...)'}
                      className="h-12 rounded-xl border-border bg-white font-inter flex-1"
                      data-testid="stripe-api-key-input"
                    />
                    <Button
                      onClick={() => saveApiKey('stripe')}
                      disabled={!stripeKey || savingKey === 'stripe'}
                      className="h-12 rounded-xl bg-primary hover:bg-primary/90"
                      data-testid="save-stripe-key-button"
                    >
                      {savingKey === 'stripe' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                    </Button>
                    {config?.stripe_api_key_set && (
                      <Button
                        variant="outline"
                        onClick={() => clearApiKey('stripe')}
                        disabled={savingKey === 'stripe'}
                        className="h-12 rounded-xl text-destructive hover:text-destructive"
                        data-testid="clear-stripe-key-button"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {config?.stripe_api_key_set && (
                    <p className="text-xs text-stone/70 font-inter">
                      {language === 'es'
                        ? 'Por seguridad, la clave actual no se muestra. Introduce una nueva para reemplazarla.'
                        : 'For security, the current key is not displayed. Enter a new one to replace it.'}
                    </p>
                  )}
                </div>


                {/* SMTP Configuration */}
                <div className="p-4 bg-muted/50 rounded-2xl space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-manrope font-semibold text-charcoal flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {language === 'es' ? 'Correo (SMTP)' : 'Email (SMTP)'}
                      </p>
                      <p className="font-inter text-sm text-stone">
                        {language === 'es' ? 'Para enviar emails (reset de contraseña, notificaciones)' : 'For sending emails (password reset, notifications)'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {config?.smtp_configured ? (
                        <span className="flex items-center gap-1 text-sage text-sm font-inter">
                          <Check className="w-4 h-4" />
                          {language === 'es' ? 'Configurado' : 'Configured'}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-500 text-sm font-inter">
                          <AlertCircle className="w-4 h-4" />
                          {language === 'es' ? 'No configurado' : 'Not configured'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Provider presets */}
                  <div>
                    <Label className="font-manrope font-semibold text-charcoal mb-2 block text-sm">
                      {language === 'es' ? 'Proveedor' : 'Provider'}
                    </Label>
                    <div className="flex gap-2">
                      {[
                        { id: 'gmail', label: 'Gmail' },
                        { id: 'hostinger', label: 'Hostinger' },
                        { id: 'custom', label: language === 'es' ? 'Personalizado' : 'Custom' },
                      ].map((p) => (
                        <button
                          key={p.id}
                          onClick={() => handleSmtpProviderChange(p.id)}
                          className={`px-4 py-2 rounded-xl text-sm font-inter transition-colors ${
                            smtpProvider === p.id
                              ? 'bg-primary text-white'
                              : 'bg-white border border-border text-charcoal hover:bg-muted'
                          }`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-stone mb-1 block">
                        {language === 'es' ? 'Servidor SMTP' : 'SMTP Server'}
                      </Label>
                      <Input
                        value={smtpHost}
                        onChange={(e) => setSmtpHost(e.target.value)}
                        placeholder="smtp.example.com"
                        className="h-10 rounded-xl border-border bg-white font-inter text-sm"
                        disabled={smtpProvider !== 'custom'}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-stone mb-1 block">
                        {language === 'es' ? 'Puerto' : 'Port'}
                      </Label>
                      <Input
                        value={smtpPort}
                        onChange={(e) => setSmtpPort(e.target.value)}
                        placeholder="465"
                        className="h-10 rounded-xl border-border bg-white font-inter text-sm"
                        disabled={smtpProvider !== 'custom'}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-stone mb-1 block">
                      {language === 'es' ? 'Usuario (email)' : 'User (email)'}
                    </Label>
                    <Input
                      type="email"
                      value={smtpUser}
                      onChange={(e) => setSmtpUser(e.target.value)}
                      placeholder={language === 'es' ? 'tu@email.com' : 'your@email.com'}
                      className="h-10 rounded-xl border-border bg-white font-inter text-sm"
                    />
                  </div>

                  <div>
                    <Label className="text-xs text-stone mb-1 block">
                      {language === 'es' ? 'Contraseña' : 'Password'}
                    </Label>
                    <Input
                      type="password"
                      value={smtpPassword}
                      onChange={(e) => setSmtpPassword(e.target.value)}
                      placeholder={smtpProvider === 'gmail'
                        ? (language === 'es' ? 'Contraseña de aplicación (16 caracteres)' : 'App password (16 characters)')
                        : (language === 'es' ? 'Contraseña del email' : 'Email password')}
                      className="h-10 rounded-xl border-border bg-white font-inter text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-stone mb-1 block">
                        {language === 'es' ? 'Email remitente (opcional)' : 'From email (optional)'}
                      </Label>
                      <Input
                        type="email"
                        value={smtpFromEmail}
                        onChange={(e) => setSmtpFromEmail(e.target.value)}
                        placeholder={language === 'es' ? 'Si difiere del usuario' : 'If different from user'}
                        className="h-10 rounded-xl border-border bg-white font-inter text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-stone mb-1 block">
                        {language === 'es' ? 'Nombre remitente' : 'From name'}
                      </Label>
                      <Input
                        value={smtpFromName}
                        onChange={(e) => setSmtpFromName(e.target.value)}
                        placeholder="MealPlanner"
                        className="h-10 rounded-xl border-border bg-white font-inter text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Label className="text-sm text-charcoal font-inter">SSL/TLS</Label>
                    <Switch
                      checked={smtpUseSsl}
                      onCheckedChange={setSmtpUseSsl}
                      disabled={smtpProvider !== 'custom'}
                    />
                    <span className="text-xs text-stone font-inter">
                      {smtpUseSsl
                        ? (language === 'es' ? 'SSL directo (puerto 465)' : 'Direct SSL (port 465)')
                        : (language === 'es' ? 'STARTTLS (puerto 587)' : 'STARTTLS (port 587)')}
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={saveSmtpConfig}
                      disabled={!smtpHost || !smtpUser || !smtpPassword || savingKey === 'smtp'}
                      className="flex-1 h-10 rounded-xl bg-primary hover:bg-primary/90"
                    >
                      {savingKey === 'smtp' ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      {language === 'es' ? 'Guardar' : 'Save'}
                    </Button>
                    {config?.smtp_configured && (
                      <>
                        <Button
                          variant="outline"
                          onClick={sendTestEmail}
                          disabled={testingEmail}
                          className="h-10 rounded-xl"
                        >
                          {testingEmail ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          {language === 'es' ? 'Probar' : 'Test'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={clearSmtpConfig}
                          disabled={savingKey === 'smtp'}
                          className="h-10 rounded-xl text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>

                  {smtpProvider === 'gmail' && (
                    <div className="text-xs text-stone/70 font-inter space-y-1">
                      <p>{language === 'es' ? 'Para obtener la contraseña de aplicación de Gmail:' : 'To get the Gmail app password:'}</p>
                      <ol className="list-decimal list-inside space-y-0.5">
                        <li>{language === 'es' ? 'Activa la verificación en 2 pasos en Google' : 'Enable 2-step verification in Google'}</li>
                        <li>{language === 'es' ? 'Ve a myaccount.google.com/apppasswords' : 'Go to myaccount.google.com/apppasswords'}</li>
                        <li>{language === 'es' ? 'Crea una contraseña de aplicación para "Correo"' : 'Create an app password for "Mail"'}</li>
                      </ol>
                    </div>
                  )}

                  {config?.smtp_configured && (
                    <p className="text-xs text-stone/70 font-inter">
                      {language === 'es'
                        ? `Emails enviados desde: ${config.smtp_from_email || config.smtp_host || 'configurado'}`
                        : `Emails sent from: ${config.smtp_from_email || config.smtp_host || 'configured'}`}
                    </p>
                  )}
                </div>
              </Card>

              {/* App Settings */}
              <Card className="bg-white rounded-3xl shadow-soft p-6 space-y-6">
                <h3 className="font-manrope font-bold text-lg text-charcoal mb-2">
                  {language === 'es' ? 'Configuración de la App' : 'App Settings'}
                </h3>

                {/* Calorie Calculation */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-2xl">
                  <div>
                    <p className="font-manrope font-semibold text-charcoal">
                      {t('admin.configOptions.calorieEnabled')}
                    </p>
                    <p className="font-inter text-sm text-stone">
                      {language === 'es' ? 'Habilita o deshabilita el cálculo de calorías con IA' : 'Enable or disable AI calorie calculation'}
                    </p>
                  </div>
                  <Switch
                    checked={config?.calorie_enabled || false}
                    onCheckedChange={(checked) => updateConfig('calorie_enabled', checked)}
                    data-testid="toggle-calorie-enabled"
                  />
                </div>

                {/* Recipe Import */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-2xl">
                  <div>
                    <p className="font-manrope font-semibold text-charcoal">
                      {language === 'es' ? 'Importación por URL habilitada' : 'Recipe import by URL enabled'}
                    </p>
                    <p className="font-inter text-sm text-stone">
                      {language === 'es' ? 'Permite a los usuarios importar recetas desde una URL' : 'Allows users to import recipes from a URL'}
                    </p>
                  </div>
                  <Switch
                    checked={config?.recipe_import_enabled || false}
                    onCheckedChange={(checked) => updateConfig('recipe_import_enabled', checked)}
                    data-testid="toggle-recipe-import-enabled"
                  />
                </div>

                {/* Audio Import Toggle */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-2xl">
                  <div>
                    <p className="font-manrope font-semibold text-charcoal">
                      {language === 'es' ? 'Importación por audio habilitada' : 'Recipe import via audio enabled'}
                    </p>
                    <p className="font-inter text-sm text-stone">
                      {language === 'es' ? 'Permite importar recetas subiendo o grabando audio' : 'Allows importing recipes by uploading or recording audio'}
                    </p>
                  </div>
                  <Switch
                    checked={config?.recipe_audio_import_enabled || false}
                    onCheckedChange={(checked) => updateConfig('recipe_audio_import_enabled', checked)}
                    data-testid="toggle-recipe-audio-import-enabled"
                  />
                </div>

                {/* Image Import Toggle */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-2xl">
                  <div>
                    <p className="font-manrope font-semibold text-charcoal">
                      {language === 'es' ? 'Importación por foto habilitada' : 'Recipe import via photo enabled'}
                    </p>
                    <p className="font-inter text-sm text-stone">
                      {language === 'es' ? 'Permite importar recetas subiendo o fotografiando la receta' : 'Allows importing recipes by uploading or taking a photo'}
                    </p>
                  </div>
                  <Switch
                    checked={config?.recipe_image_import_enabled || false}
                    onCheckedChange={(checked) => updateConfig('recipe_image_import_enabled', checked)}
                    data-testid="toggle-recipe-image-import-enabled"
                  />
                </div>

                {/* Video Import Toggle */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-2xl">
                  <div>
                    <p className="font-manrope font-semibold text-charcoal">
                      {language === 'es' ? 'Importación por vídeo habilitada' : 'Recipe import via video enabled'}
                    </p>
                    <p className="font-inter text-sm text-stone">
                      {language === 'es' ? 'Permite importar recetas desde vídeos de YouTube' : 'Allows importing recipes from YouTube videos'}
                    </p>
                  </div>
                  <Switch
                    checked={config?.recipe_video_import_enabled || false}
                    onCheckedChange={(checked) => updateConfig('recipe_video_import_enabled', checked)}
                    data-testid="toggle-recipe-video-import-enabled"
                  />
                </div>

                {/* YouTube Cookies Upload */}
                <div className="p-4 bg-muted/50 rounded-2xl space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-manrope font-semibold text-charcoal">
                        {language === 'es' ? 'Cookies de YouTube' : 'YouTube Cookies'}
                      </p>
                      <p className="font-inter text-sm text-stone">
                        {language === 'es'
                          ? 'Necesarias para que la importación desde vídeo funcione en producción'
                          : 'Required for video import to work in production'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {cookiesFileExists && (
                        <span className="flex items-center gap-1 text-xs text-sage font-medium">
                          <Check className="w-3.5 h-3.5" />
                          {language === 'es' ? 'Activo' : 'Active'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <input
                      ref={cookiesInputRef}
                      type="file"
                      accept=".txt"
                      className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadCookies(f); e.target.value = ''; }}
                    />
                    <div className="flex-1">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full rounded-xl"
                        disabled={uploadingCookies}
                        onClick={() => cookiesInputRef.current?.click()}
                      >
                        {uploadingCookies
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{language === 'es' ? 'Subiendo...' : 'Uploading...'}</>
                          : <><Upload className="w-4 h-4 mr-2" />{language === 'es' ? 'Subir cookies.txt' : 'Upload cookies.txt'}</>
                        }
                      </Button>
                    </div>
                    {cookiesFileExists && (
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-xl text-red-500 border-red-200 hover:bg-red-50"
                        onClick={handleDeleteCookies}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* AI Image Generation Toggle */}
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-2xl">
                  <div>
                    <p className="font-manrope font-semibold text-charcoal">
                      {language === 'es' ? 'Generación de imágenes con IA (DALL-E 2)' : 'AI image generation (DALL-E 2)'}
                    </p>
                    <p className="font-inter text-sm text-stone">
                      {language === 'es' ? 'Genera automáticamente una imagen con IA para recetas del plan Premium que no tengan imagen' : 'Automatically generates an AI image for Premium plan recipes without an image'}
                    </p>
                  </div>
                  <Switch
                    checked={config?.pexels_enabled || false}
                    onCheckedChange={(checked) => updateConfig('pexels_enabled', checked)}
                    data-testid="toggle-pexels-enabled"
                  />
                </div>


                {/* Plan Limits */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-manrope font-semibold text-charcoal">
                      {t('admin.configOptions.freeMaxRecipes')} (Free)
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      value={config?.free_max_recipes || 20}
                      onChange={(e) => updateConfig('free_max_recipes', parseInt(e.target.value))}
                      className="h-12 rounded-xl border-border bg-white/50 font-inter"
                      data-testid="free-max-recipes-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-manrope font-semibold text-charcoal">
                      {t('admin.configOptions.freeMaxMenus')} (Free)
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      value={config?.free_max_menus || 5}
                      onChange={(e) => updateConfig('free_max_menus', parseInt(e.target.value))}
                      className="h-12 rounded-xl border-border bg-white/50 font-inter"
                      data-testid="free-max-menus-input"
                    />
                  </div>
                </div>

                {/* Subscription Prices */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label className="font-manrope font-semibold text-charcoal">
                      {language === 'es' ? 'Precio Pro mensual' : 'Pro monthly price'}
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={config?.pro_monthly_price ?? 5.99}
                        onChange={(e) => updateConfig('pro_monthly_price', parseFloat(e.target.value))}
                        className="h-12 rounded-xl border-border bg-white/50 font-inter pl-8"
                        data-testid="pro-monthly-price-input"
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone font-inter">£</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-manrope font-semibold text-charcoal">
                      {language === 'es' ? 'Descuento Pro anual (%)' : 'Pro yearly discount (%)'}
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={config?.pro_yearly_discount ?? 20}
                        onChange={(e) => updateConfig('pro_yearly_discount', parseInt(e.target.value))}
                        className="h-12 rounded-xl border-border bg-white/50 font-inter pl-8"
                        data-testid="pro-yearly-discount-input"
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone font-inter">%</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-manrope font-semibold text-charcoal">
                      {language === 'es' ? 'Precio Premium mensual' : 'Premium monthly price'}
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={config?.premium_monthly_price ?? 9.99}
                        onChange={(e) => updateConfig('premium_monthly_price', parseFloat(e.target.value))}
                        className="h-12 rounded-xl border-border bg-white/50 font-inter pl-8"
                        data-testid="premium-monthly-price-input"
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone font-inter">£</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-manrope font-semibold text-charcoal">
                      {language === 'es' ? 'Descuento Premium anual (%)' : 'Premium yearly discount (%)'}
                    </Label>
                    <div className="relative">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={config?.premium_yearly_discount ?? 20}
                        onChange={(e) => updateConfig('premium_yearly_discount', parseInt(e.target.value))}
                        className="h-12 rounded-xl border-border bg-white/50 font-inter pl-8"
                        data-testid="premium-yearly-discount-input"
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone font-inter">%</span>
                    </div>
                  </div>
                </div>

                {/* Stripe Price IDs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-4 bg-muted/30 rounded-2xl border border-border/50">
                  <div className="col-span-full mb-2">
                    <p className="font-manrope font-semibold text-charcoal">
                      {language === 'es' ? 'IDs de precios de Stripe' : 'Stripe Price IDs'}
                    </p>
                    <p className="font-inter text-xs text-stone">
                      {language === 'es' 
                        ? 'Configura los IDs de Stripe para cada plan. Estos IDs se encuentran en el Dashboard de Stripe.' 
                        : 'Configure Stripe IDs for each plan. These IDs are found in the Stripe Dashboard.'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-stone">Pro Monthly Price ID</Label>
                    <Input
                      value={priceProMonthly}
                      onChange={(e) => setPriceProMonthly(e.target.value)}
                      onBlur={() => updateConfig('stripe_price_pro_monthly', priceProMonthly)}
                      placeholder="price_..."
                      className="h-10 rounded-xl border-border bg-white font-inter text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-stone">Pro Yearly Price ID</Label>
                    <Input
                      value={priceProYearly}
                      onChange={(e) => setPriceProYearly(e.target.value)}
                      onBlur={() => updateConfig('stripe_price_pro_yearly', priceProYearly)}
                      placeholder="price_..."
                      className="h-10 rounded-xl border-border bg-white font-inter text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-stone">Premium Monthly Price ID</Label>
                    <Input
                      value={pricePremiumMonthly}
                      onChange={(e) => setPricePremiumMonthly(e.target.value)}
                      onBlur={() => updateConfig('stripe_price_premium_monthly', pricePremiumMonthly)}
                      placeholder="price_..."
                      className="h-10 rounded-xl border-border bg-white font-inter text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-stone">Premium Yearly Price ID</Label>
                    <Input
                      value={pricePremiumYearly}
                      onChange={(e) => setPricePremiumYearly(e.target.value)}
                      onBlur={() => updateConfig('stripe_price_premium_yearly', pricePremiumYearly)}
                      placeholder="price_..."
                      className="h-10 rounded-xl border-border bg-white font-inter text-sm"
                    />
                  </div>
                </div>

                {/* AI Models & cost controls */}
                <div className="space-y-3 mt-6 pt-6 border-t border-border">
                  <Label className="font-manrope font-bold text-charcoal text-lg">
                    {language === 'es' ? 'IA y modelos' : 'AI & models'}
                  </Label>
                  <p className="font-inter text-sm text-stone">
                    {language === 'es'
                      ? 'Modelo para tareas ligeras (importar, ingredientes, macros) y para tareas complejas (planes, menú IA).'
                      : 'Model for light tasks (import, ingredients, macros) and heavy tasks (plans, AI menu).'}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-stone">{language === 'es' ? 'Modelo recetas (ligero)' : 'Recipes model (light)'}</Label>
                      <select
                        value={config?.ai_model_recipes || 'gpt-4o-mini'}
                        onChange={(e) => updateConfig('ai_model_recipes', e.target.value)}
                        className="h-11 w-full rounded-xl border border-border bg-white px-3 text-sm"
                        data-testid="ai-model-recipes-input"
                      >
                        {[...new Set([config?.ai_model_recipes, ...AI_MODEL_OPTIONS].filter(Boolean))].map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-stone">{language === 'es' ? 'Modelo planes (potente)' : 'Planning model (powerful)'}</Label>
                      <select
                        value={config?.ai_model_planning || 'gpt-4o'}
                        onChange={(e) => updateConfig('ai_model_planning', e.target.value)}
                        className="h-11 w-full rounded-xl border border-border bg-white px-3 text-sm"
                        data-testid="ai-model-planning-input"
                      >
                        {[...new Set([config?.ai_model_planning, ...AI_MODEL_OPTIONS].filter(Boolean))].map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-stone">{language === 'es' ? 'Cupo planes/mes (usuario)' : 'Plan generations/month (user)'}</Label>
                      <Input
                        type="number" min="0"
                        value={config?.max_plan_generations_month ?? 10}
                        onChange={(e) => updateConfig('max_plan_generations_month', parseInt(e.target.value))}
                        className="h-11 rounded-xl border-border bg-white font-inter text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-stone">{language === 'es' ? 'Cupo menús IA/mes (usuario)' : 'AI menus/month (user)'}</Label>
                      <Input
                        type="number" min="0"
                        value={config?.max_menu_ai_generations_month ?? 30}
                        onChange={(e) => updateConfig('max_menu_ai_generations_month', parseInt(e.target.value))}
                        className="h-11 rounded-xl border-border bg-white font-inter text-sm"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer mt-1">
                    <input
                      type="checkbox"
                      checked={config?.ai_cache_enabled ?? true}
                      onChange={(e) => updateConfig('ai_cache_enabled', e.target.checked)}
                      className="w-4 h-4 accent-primary"
                      data-testid="ai-cache-toggle"
                    />
                    <span className="font-inter text-sm text-charcoal">
                      {language === 'es' ? 'Caché de IA activada (reutiliza respuestas idénticas)' : 'AI cache enabled (reuse identical responses)'}
                    </span>
                  </label>
                </div>

                {/* ExerciseDB API key */}
                <div className="space-y-2 mt-6 pt-6 border-t border-border">
                  <Label className="font-manrope font-bold text-charcoal text-lg">ExerciseDB</Label>
                  <p className="font-inter text-sm text-stone">
                    {language === 'es'
                      ? 'API key de RapidAPI para importar ejercicios con imágenes.'
                      : 'RapidAPI key to import exercises with images.'}
                    {config?.exercisedb_api_key_set && (
                      <span className="text-sage ml-1">✓ {language === 'es' ? 'configurada' : 'set'}</span>
                    )}
                  </p>
                  <Input
                    type="password"
                    defaultValue=""
                    onBlur={(e) => { if (e.target.value) { updateConfig('exercisedb_api_key', e.target.value); e.target.value = ''; } }}
                    placeholder={language === 'es' ? 'Introduce tu RapidAPI key' : 'Enter your RapidAPI key'}
                    className="h-11 rounded-xl border-border bg-white font-inter text-sm max-w-md"
                    data-testid="exercisedb-key-input"
                  />
                </div>

                {/* Audit Retention */}
                <div className="space-y-2 mt-4">
                  <Label className="font-manrope font-semibold text-charcoal">
                    {language === 'es' ? 'Días de retención de auditoría' : 'Audit retention days'}
                  </Label>
                  <div className="flex items-center gap-4">
                    <Input
                      type="number"
                      min="1"
                      max="3650"
                      value={config?.audit_retention_days || 90}
                      onChange={(e) => updateConfig('audit_retention_days', parseInt(e.target.value))}
                      className="h-12 w-32 rounded-xl border-border bg-white/50 font-inter"
                      data-testid="audit-retention-input"
                    />
                    <p className="font-inter text-sm text-stone italic">
                      {language === 'es'
                        ? 'Los registros más antiguos se eliminarán automáticamente una vez al día.'
                        : 'Older logs will be automatically deleted once a day.'}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Audit Tab */}
          <TabsContent value="audit">
            <Card className="bg-white rounded-3xl shadow-soft p-6 space-y-6 overflow-hidden">
              <div className="flex flex-col md:flex-row gap-4 items-end justify-between">
                <form onSubmit={handleAuditSearch} className="flex-1 flex gap-2 w-full">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone" />
                    <Input
                      placeholder={language === 'es' ? 'Buscar por email o acción...' : 'Search by email or action...'}
                      value={auditQuery}
                      onChange={(e) => setAuditQuery(e.target.value)}
                      className="pl-10 h-11 rounded-xl"
                    />
                  </div>
                  <Button type="submit" variant="outline" className="h-11 rounded-xl">
                    {language === 'es' ? 'Buscar' : 'Search'}
                  </Button>
                </form>

                <div className="flex gap-2 w-full md:w-auto">
                  <div className="relative flex-1 md:w-48">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone" />
                    <select
                      value={auditAction}
                      onChange={(e) => {
                        setAuditAction(e.target.value);
                        setAuditSkip(0);
                      }}
                      className="pl-10 h-11 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background appearance-none"
                    >
                      <option value="">{language === 'es' ? 'Todas las acciones' : 'All actions'}</option>
                      <option value="LOGIN">LOGIN</option>
                      <option value="LOGIN_GOOGLE">LOGIN_GOOGLE</option>
                      <option value="REGISTER">REGISTER</option>
                      <option value="REGISTER_GOOGLE">REGISTER_GOOGLE</option>
                      <option value="FORGOT_PASSWORD_REQUEST">FORGOT_PASSWORD_REQUEST</option>
                      <option value="UPDATE_CONFIG">UPDATE_CONFIG</option>
                      <option value="UPDATE_USER_PLAN">UPDATE_USER_PLAN</option>
                      <option value="DELETE_USER">DELETE_USER</option>
                      <option value="PEXELS_SEARCH">PEXELS_SEARCH</option>
                      <option value="RECIPE_IMPORT_START">RECIPE_IMPORT_START</option>
                      <option value="RECIPE_IMPORT_SUCCESS">RECIPE_IMPORT_SUCCESS</option>
                      <option value="RECIPE_IMPORT_FAILED">RECIPE_IMPORT_FAILED</option>
                      <option value="CALORIE_ESTIMATION">CALORIE_ESTIMATION</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-4 font-manrope font-semibold text-charcoal">Fecha</th>
                      <th className="text-left p-4 font-manrope font-semibold text-charcoal">Usuario</th>
                      <th className="text-left p-4 font-manrope font-semibold text-charcoal">Acción</th>
                      <th className="text-left p-4 font-manrope font-semibold text-charcoal">Detalles</th>
                      <th className="text-left p-4 font-manrope font-semibold text-charcoal">IP</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {loadingLogs ? (
                      <tr>
                        <td colSpan="5" className="p-10 text-center text-stone font-inter">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-primary" />
                          Cargando registros...
                        </td>
                      </tr>
                    ) : auditLogs.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="p-10 text-center text-stone font-inter">
                          No se encontraron registros de auditoría.
                        </td>
                      </tr>
                    ) : (
                      auditLogs.map((log) => (
                        <tr key={log.log_id} className="hover:bg-muted/30 transition-colors">
                          <td className="p-4 font-inter text-sm whitespace-nowrap">
                            {format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm')}
                          </td>
                          <td className="p-4">
                            <span className="font-manrope text-sm font-medium text-charcoal block truncate max-w-[150px]" title={log.user_email}>
                              {log.user_email || '-'}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-manrope font-bold uppercase ${log.severity === 'security' || log.severity === 'error' ? 'bg-destructive/10 text-destructive' :
                              log.severity === 'warning' ? 'bg-amber-100 text-amber-700' :
                                'bg-sage/10 text-sage'
                              }`}>
                              {log.action}
                            </span>
                          </td>
                          <td className="p-4 max-w-[200px]">
                            <div className="flex items-center gap-2">
                              <p className="font-inter text-xs text-stone truncate cursor-default flex-1" title={JSON.stringify(log.details, null, 2)}>
                                {Object.entries(log.details).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(', ') || '-'}
                              </p>
                              {Object.keys(log.details).length > 0 && (
                                <button
                                  onClick={() => {
                                    setSelectedLog(log);
                                    setDetailsDialogOpen(true);
                                  }}
                                  className="p-1 hover:bg-muted rounded text-stone hover:text-charcoal transition-colors"
                                  title={language === 'es' ? 'Ver detalles completos' : 'View full details'}
                                >
                                  <Search className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="p-4 font-inter text-xs text-stone font-mono">
                            {log.ip_address || '-'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {auditTotal > auditLimit && (
                <div className="flex items-center justify-between border-t border-border pt-4">
                  <p className="text-sm font-inter text-stone">
                    Mostrando {auditSkip + 1}-{Math.min(auditSkip + auditLimit, auditTotal)} de {auditTotal}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={auditSkip === 0}
                      onClick={() => setAuditSkip(Math.max(0, auditSkip - auditLimit))}
                      className="h-9 px-3 rounded-lg"
                    >
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={auditSkip + auditLimit >= auditTotal}
                      onClick={() => setAuditSkip(auditSkip + auditLimit)}
                      className="h-9 px-3 rounded-lg"
                    >
                      Siguiente
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-white rounded-2xl shadow-soft p-6 text-center">
                <p className="font-manrope font-bold text-3xl text-primary mb-1">
                  {stats?.total_users || 0}
                </p>
                <p className="font-inter text-sm text-stone">{t('admin.totalUsers')}</p>
              </Card>
              <Card className="bg-white rounded-2xl shadow-soft p-6 text-center">
                <p className="font-manrope font-bold text-3xl text-sage mb-1">
                  {stats?.premium_users || 0}
                </p>
                <p className="font-inter text-sm text-stone">{t('admin.premiumUsers')}</p>
              </Card>
              <Card className="bg-white rounded-2xl shadow-soft p-6 text-center">
                <p className="font-manrope font-bold text-3xl text-charcoal mb-1">
                  {stats?.total_recipes || 0}
                </p>
                <p className="font-inter text-sm text-stone">{t('admin.totalRecipes')}</p>
              </Card>
              <Card className="bg-white rounded-2xl shadow-soft p-6 text-center">
                <p className="font-manrope font-bold text-3xl text-charcoal mb-1">
                  {stats?.total_menus || 0}
                </p>
                <p className="font-inter text-sm text-stone">{t('admin.totalMenus')}</p>
              </Card>
            </div>
          </TabsContent>

          {/* Food Categories Tab */}
          <TabsContent value="categories">
            <Card className="bg-white rounded-3xl shadow-soft p-6 space-y-6">
              <div>
                <h2 className="font-manrope font-bold text-xl text-charcoal mb-1">
                  {language === 'es' ? 'Categorías de alimento' : 'Food categories'}
                </h2>
                <p className="font-inter text-sm text-stone">
                  {language === 'es'
                    ? 'Los usuarios asignan estas categorías a sus recetas para que el menú sea más variado.'
                    : 'Users assign these categories to their recipes so that the menu is more varied.'}
                </p>
              </div>

              {/* Add new category form */}
              <div className="bg-muted/30 rounded-2xl p-4 space-y-3">
                <p className="font-manrope font-semibold text-sm text-charcoal">
                  {language === 'es' ? 'Nueva categoría' : 'New category'}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs text-stone">{language === 'es' ? 'Nombre (ES)' : 'Name (ES)'}</Label>
                    <Input
                      value={newCatEs}
                      onChange={e => setNewCatEs(e.target.value)}
                      placeholder={language === 'es' ? 'ej. Verduras' : 'e.g. Verduras'}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs text-stone">{language === 'es' ? 'Nombre (EN)' : 'Name (EN)'}</Label>
                    <Input
                      value={newCatEn}
                      onChange={e => setNewCatEn(e.target.value)}
                      placeholder={language === 'es' ? 'ej. Vegetables' : 'e.g. Vegetables'}
                      className="rounded-xl"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={createFoodCategory}
                      disabled={savingCat || !newCatEs.trim() || !newCatEn.trim()}
                      className="rounded-xl w-full sm:w-auto"
                    >
                      {savingCat ? <Loader2 className="w-4 h-4 animate-spin" /> : (language === 'es' ? 'Añadir' : 'Add')}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Categories list */}
              {loadingCategories ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : foodCategories.length === 0 ? (
                <div className="text-center py-10 text-stone font-inter text-sm">
                  {language === 'es' ? 'No hay categorías todavía.' : 'No categories yet.'}
                </div>
              ) : (
                <div className="space-y-2">
                  {foodCategories.map(cat => (
                    <div key={cat.category_id} className="flex items-center gap-3 bg-muted/20 rounded-2xl px-4 py-3 border border-border/50">
                      {editingCat?.category_id === cat.category_id ? (
                        <>
                          <Input
                            value={editingCat.name_es}
                            onChange={e => setEditingCat({ ...editingCat, name_es: e.target.value })}
                            className="rounded-xl flex-1 h-8 text-sm"
                            placeholder="ES"
                          />
                          <Input
                            value={editingCat.name_en}
                            onChange={e => setEditingCat({ ...editingCat, name_en: e.target.value })}
                            className="rounded-xl flex-1 h-8 text-sm"
                            placeholder="EN"
                          />
                          <Button
                            size="sm"
                            onClick={updateFoodCategory}
                            disabled={savingCat}
                            className="rounded-xl h-8 px-3"
                          >
                            {savingCat ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                          </Button>
                          <button
                            onClick={() => setEditingCat(null)}
                            className="p-1.5 rounded-lg hover:bg-muted transition-colors text-stone"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <Tag className="w-4 h-4 text-primary shrink-0" strokeWidth={1.5} />
                          <span className="font-inter text-sm text-charcoal flex-1">{cat.name_es}</span>
                          <span className="font-inter text-xs text-stone">{cat.name_en}</span>
                          <button
                            onClick={() => setEditingCat({ ...cat })}
                            className="p-1.5 rounded-lg hover:bg-muted transition-colors text-stone hover:text-primary"
                            title={language === 'es' ? 'Editar' : 'Edit'}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => deleteFoodCategory(cat.category_id)}
                            disabled={deletingCatId === cat.category_id}
                            className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-stone hover:text-red-500"
                            title={language === 'es' ? 'Eliminar' : 'Delete'}
                          >
                            {deletingCatId === cat.category_id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Trash2 className="w-3.5 h-3.5" />
                            }
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="emails">
            <div className="space-y-4">
              <div className="mb-2">
                <h2 className="font-manrope font-bold text-xl text-charcoal mb-1">
                  {language === 'es' ? 'Plantillas de email' : 'Email templates'}
                </h2>
                <p className="font-inter text-sm text-stone">
                  {language === 'es'
                    ? 'Personaliza el texto de los emails automáticos. Soportan HTML. Guarda los cambios y usa "Probar" para enviarte un email de prueba.'
                    : 'Customise the text of automatic emails. HTML is supported. Save changes and use "Test" to send yourself a test email.'}
                </p>
              </div>

              {['verification', 'welcome', 'reset_password', 'resend_verification', 'email_change'].map((templateId) => {
                const tpl = emailTemplates[templateId];
                if (!tpl) return null;
                const edits = templateEdits[templateId] || {};
                const lang = activeTemplateLang[templateId] || 'es';
                const isEs = lang === 'es';

                return (
                  <Card key={templateId} className="bg-white rounded-3xl shadow-soft p-6 space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Mail className="w-4 h-4 text-primary shrink-0" strokeWidth={1.5} />
                          <h3 className="font-manrope font-bold text-base text-charcoal">
                            {isEs ? tpl.label_es : tpl.label_en}
                          </h3>
                          {tpl.customized && (
                            <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-inter rounded-full">
                              {language === 'es' ? 'Personalizada' : 'Customized'}
                            </span>
                          )}
                        </div>
                        <p className="font-inter text-xs text-stone mt-1 ml-6">
                          {language === 'es' ? 'Variables: ' : 'Variables: '}
                          <span className="font-mono text-primary/80">{tpl.placeholders?.join(', ')}</span>
                        </p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => testEmailTemplate(templateId)}
                          disabled={testingTemplateId === templateId}
                          className="h-8 rounded-xl text-stone hover:text-charcoal"
                        >
                          {testingTemplateId === templateId
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                            : <Send className="w-3.5 h-3.5 mr-1" />}
                          {language === 'es' ? 'Probar' : 'Test'}
                        </Button>
                        {tpl.customized && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => resetEmailTemplate(templateId)}
                            className="h-8 rounded-xl text-stone hover:text-destructive"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            {language === 'es' ? 'Restaurar' : 'Reset'}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => saveEmailTemplate(templateId)}
                          disabled={savingTemplateId === templateId}
                          className="h-8 rounded-xl"
                        >
                          {savingTemplateId === templateId
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                            : <Save className="w-3.5 h-3.5 mr-1" />}
                          {language === 'es' ? 'Guardar' : 'Save'}
                        </Button>
                      </div>
                    </div>

                    {/* Language toggle */}
                    <div className="flex gap-2">
                      {['es', 'en'].map(l => (
                        <button
                          key={l}
                          type="button"
                          onClick={() => setActiveTemplateLang(prev => ({ ...prev, [templateId]: l }))}
                          className={`px-4 py-1.5 rounded-xl text-sm font-inter transition-colors ${
                            lang === l ? 'bg-primary text-white' : 'bg-muted/60 text-stone hover:bg-muted'
                          }`}
                        >
                          {l.toUpperCase()}
                        </button>
                      ))}
                    </div>

                    {/* Fields */}
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-stone mb-1 block">{language === 'es' ? 'Asunto' : 'Subject'}</Label>
                        <Input
                          value={isEs ? (edits.subject_es ?? '') : (edits.subject_en ?? '')}
                          onChange={e => {
                            const field = isEs ? 'subject_es' : 'subject_en';
                            setTemplateEdits(prev => ({ ...prev, [templateId]: { ...prev[templateId], [field]: e.target.value } }));
                          }}
                          className="rounded-xl text-sm h-10"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-stone mb-1 block">{language === 'es' ? 'Título (dentro del email)' : 'Title (inside email)'}</Label>
                        <Input
                          value={isEs ? (edits.title_es ?? '') : (edits.title_en ?? '')}
                          onChange={e => {
                            const field = isEs ? 'title_es' : 'title_en';
                            setTemplateEdits(prev => ({ ...prev, [templateId]: { ...prev[templateId], [field]: e.target.value } }));
                          }}
                          className="rounded-xl text-sm h-10"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-stone mb-1 block">{language === 'es' ? 'Cuerpo (HTML)' : 'Body (HTML)'}</Label>
                        <Textarea
                          value={isEs ? (edits.body_es ?? '') : (edits.body_en ?? '')}
                          onChange={e => {
                            const field = isEs ? 'body_es' : 'body_en';
                            setTemplateEdits(prev => ({ ...prev, [templateId]: { ...prev[templateId], [field]: e.target.value } }));
                          }}
                          rows={8}
                          className="rounded-xl font-mono text-xs resize-y"
                        />
                        <p className="text-xs text-stone/60 font-inter mt-1">
                          {language === 'es'
                            ? `HTML soportado. Variables disponibles: ${tpl.placeholders?.join(', ')}`
                            : `HTML supported. Available variables: ${tpl.placeholders?.join(', ')}`}
                        </p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Exercises Tab */}
          <TabsContent value="exercises">
            <Card className="bg-white rounded-3xl shadow-soft p-6 space-y-6">
              <div>
                <h2 className="font-manrope font-bold text-lg text-charcoal mb-1">
                  {language === 'es' ? 'Biblioteca de ejercicios' : 'Exercise library'}
                </h2>
                <p className="text-sm text-stone font-inter">
                  {language === 'es'
                    ? 'Importa ejercicios de ExerciseDB; la IA traduce y añade la técnica y errores comunes.'
                    : 'Import exercises from ExerciseDB; AI translates and adds technique and common mistakes.'}
                </p>
              </div>

              {!config?.exercisedb_api_key_set && (
                <p className="text-sm text-primary bg-primary/5 rounded-xl p-3">
                  {language === 'es'
                    ? 'Configura primero la API key de ExerciseDB en la pestaña Configuración.'
                    : 'Set the ExerciseDB API key in the Config tab first.'}
                </p>
              )}

              <div className="flex flex-wrap gap-3 items-end">
                <div className="space-y-1">
                  <Label className="text-xs text-stone">{language === 'es' ? 'Cantidad' : 'Amount'}</Label>
                  <Input type="number" min="1" max="50" value={importLimit} onChange={(e) => setImportLimit(e.target.value)} className="h-11 w-24 rounded-xl border-border bg-white" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-stone">{language === 'es' ? 'Zona (opcional)' : 'Body part (optional)'}</Label>
                  <select value={importBodyPart} onChange={(e) => setImportBodyPart(e.target.value)} className="h-11 rounded-xl border border-border bg-white px-3 text-sm">
                    <option value="">{language === 'es' ? 'Todas' : 'All'}</option>
                    {['back', 'chest', 'shoulders', 'upper arms', 'biceps', 'triceps', 'forearms', 'waist', 'hips', 'thighs', 'quadriceps', 'hamstrings', 'calves', 'neck', 'full body'].map(bp => (
                      <option key={bp} value={bp}>{bp}</option>
                    ))}
                  </select>
                </div>
                <Button onClick={handleGenerateBase} disabled={importingExercises || !config?.exercisedb_api_key_set} className="rounded-full bg-primary h-11" data-testid="generate-base-exercises">
                  {importingExercises ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {language === 'es' ? 'Generar biblioteca base' : 'Generate base library'}
                </Button>
              </div>

              <div className="flex gap-2 max-w-md">
                <Input value={exerciseSearch} onChange={(e) => setExerciseSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') searchExercises(); }} placeholder={language === 'es' ? 'Buscar ejercicio...' : 'Search exercise...'} className="h-11 rounded-xl border-border bg-white" />
                <Button variant="outline" onClick={searchExercises} className="h-11 rounded-xl">{language === 'es' ? 'Buscar' : 'Search'}</Button>
              </div>

              <p className="text-sm text-stone">{language === 'es' ? 'Total' : 'Total'}: <b>{exerciseTotal}</b></p>

              {loadingExercises ? (
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              ) : (
                <div className="space-y-2">
                  {exercises.map(ex => (
                    <div key={ex.exercise_id} className="flex items-center gap-3 border border-border rounded-2xl p-3">
                      <button onClick={() => setSelectedExercise(ex)} className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity">
                        {ex.image_url ? (
                          <img src={ex.image_url} alt={ex.name_es} className="w-14 h-14 rounded-lg object-cover bg-muted shrink-0" />
                        ) : (
                          <div className="w-14 h-14 rounded-lg bg-muted flex items-center justify-center shrink-0"><Dumbbell className="w-5 h-5 text-stone" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-manrope font-semibold text-charcoal truncate flex items-center gap-1.5">
                            <span className="truncate">{ex.name_es || ex.name_en}</span>
                            {ex.video_url
                              ? <span title={language === 'es' ? 'Con vídeo' : 'Has video'} className="inline-flex items-center gap-0.5 text-xs text-sage shrink-0"><Video className="w-3.5 h-3.5" /></span>
                              : <span title={language === 'es' ? 'Sin vídeo' : 'No video'} className="inline-flex items-center text-xs text-stone/40 shrink-0"><Video className="w-3.5 h-3.5" /></span>}
                          </p>
                          <p className="text-xs text-stone truncate">{[(ex.muscle_groups || []).join(', '), (ex.equipment || []).join(', '), ex.difficulty].filter(Boolean).join(' · ')}</p>
                        </div>
                      </button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteExercise(ex.exercise_id)} className="rounded-full text-destructive"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                  {exercises.length === 0 && (
                    <p className="text-sm text-stone italic">{language === 'es' ? 'No hay ejercicios todavía. Importa la biblioteca base.' : 'No exercises yet. Import the base library.'}</p>
                  )}
                </div>
              )}

              {exerciseTotal > EX_PAGE_SIZE && (
                <div className="flex items-center justify-center gap-3 pt-2">
                  <Button variant="outline" size="sm" disabled={exercisePage === 0 || loadingExercises} onClick={() => fetchExercises(exercisePage - 1)} className="rounded-full">
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-stone font-inter">
                    {language === 'es' ? 'Página' : 'Page'} {exercisePage + 1} / {Math.max(1, Math.ceil(exerciseTotal / EX_PAGE_SIZE))}
                  </span>
                  <Button variant="outline" size="sm" disabled={(exercisePage + 1) * EX_PAGE_SIZE >= exerciseTotal || loadingExercises} onClick={() => fetchExercises(exercisePage + 1)} className="rounded-full">
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </Card>

            {/* Exercise detail dialog */}
            <Dialog open={!!selectedExercise} onOpenChange={(open) => { if (!open) setSelectedExercise(null); }}>
              <DialogContent className="rounded-3xl max-w-lg max-h-[88vh] overflow-y-auto">
                {selectedExercise && (
                  <>
                    <DialogHeader>
                      <DialogTitle className="font-manrope">{selectedExercise.name_es || selectedExercise.name_en}</DialogTitle>
                    </DialogHeader>
                    {selectedExercise.video_url ? (
                      <video controls playsInline poster={selectedExercise.image_hd || selectedExercise.image_url} className="w-full rounded-2xl bg-muted mb-3">
                        <source src={selectedExercise.video_url} type="video/mp4" />
                      </video>
                    ) : (
                      (selectedExercise.image_hd || selectedExercise.image_url) && <img src={selectedExercise.image_hd || selectedExercise.image_url} alt={selectedExercise.name_es} className="w-full rounded-2xl bg-muted mb-3" />
                    )}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(selectedExercise.muscle_groups || []).map(m => <span key={m} className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full">{m}</span>)}
                      {(selectedExercise.equipment || []).map(eq => <span key={eq} className="text-xs bg-muted px-3 py-1 rounded-full text-stone">{eq}</span>)}
                      {selectedExercise.difficulty && <span className="text-xs bg-sage/10 text-sage px-3 py-1 rounded-full">{selectedExercise.difficulty}</span>}
                    </div>
                    {selectedExercise.instructions_es && (
                      <div className="mb-3">
                        <p className="font-manrope font-semibold text-charcoal mb-1">{language === 'es' ? 'Instrucciones' : 'Instructions'}</p>
                        <NumberedText text={selectedExercise.instructions_es} />
                      </div>
                    )}
                    {selectedExercise.common_mistakes_es && (
                      <div>
                        <p className="font-manrope font-semibold text-charcoal mb-1">{language === 'es' ? 'Errores comunes' : 'Common mistakes'}</p>
                        <NumberedText text={selectedExercise.common_mistakes_es} />
                      </div>
                    )}
                  </>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>

        {/* Delete User Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-manrope text-charcoal">
                {t('admin.deleteUser')}
              </DialogTitle>
            </DialogHeader>
            <p className="font-inter text-stone py-4">
              {t('admin.deleteUserConfirm')}
            </p>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                className="rounded-full"
              >
                {t('cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={deleteUser}
                className="rounded-full"
                data-testid="confirm-delete-user-button"
              >
                {t('delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Log Details Dialog */}
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="rounded-2xl max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="font-manrope text-charcoal flex items-center justify-between">
                <span>{language === 'es' ? 'Detalles del registro' : 'Log Details'}</span>
                <span className={`ml-4 px-2.5 py-0.5 rounded-full text-[10px] font-manrope font-bold uppercase ${selectedLog?.severity === 'security' || selectedLog?.severity === 'error' ? 'bg-destructive/10 text-destructive' :
                  selectedLog?.severity === 'warning' ? 'bg-amber-100 text-amber-700' :
                    'bg-sage/10 text-sage'
                  }`}>
                  {selectedLog?.action}
                </span>
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto py-4 space-y-4 font-inter text-sm">
              <div className="grid grid-cols-2 gap-4 border-b border-border pb-4 text-stone">
                <div>
                  <p className="text-xs font-semibold uppercase">{language === 'es' ? 'Fecha' : 'Date'}</p>
                  <p>{selectedLog && format(new Date(selectedLog.timestamp), 'dd/MM/yyyy HH:mm:ss')}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase">{language === 'es' ? 'Usuario' : 'User'}</p>
                  <p className="truncate">{selectedLog?.user_email || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase">IP</p>
                  <p>{selectedLog?.ip_address || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase">User Agent</p>
                  <p className="truncate max-w-[200px]" title={selectedLog?.user_agent}>{selectedLog?.user_agent || '-'}</p>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase text-stone">{language === 'es' ? 'Datos Detallados' : 'Detailed Data'}</p>
                {selectedLog && Object.entries(selectedLog.details).map(([key, value]) => {
                  const isTechnicalField = ['prompt', 'response', 'ai_prompt', 'ai_response', 'error', 'enhanced_query'].includes(key);
                  const isUrl = typeof value === 'string' && (value.startsWith('http') || value.startsWith('/api/uploads'));
                  const displayValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);

                  const copyToClipboard = (text, fieldKey) => {
                    navigator.clipboard.writeText(text);
                    setCopiedField(fieldKey);
                    setTimeout(() => setCopiedField(null), 2000);
                    toast.success(language === 'es' ? 'Copiado al portapapeles' : 'Copied to clipboard');
                  };

                  return (
                    <div key={key} className="bg-muted/30 p-4 rounded-2xl space-y-2 border border-border/50 relative group">
                      <div className="flex items-center justify-between px-1">
                        <p className="font-manrope font-bold text-xs uppercase tracking-wider text-charcoal/60">
                          {key.replace(/_/g, ' ')}
                        </p>
                        {!isUrl && (
                          <button
                            onClick={() => copyToClipboard(displayValue, key)}
                            className="p-1.5 rounded-lg hover:bg-white transition-colors text-stone hover:text-primary"
                            title={language === 'es' ? 'Copiar valor' : 'Copy value'}
                          >
                            {copiedField === key ? (
                              <Check className="w-3.5 h-3.5" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                      </div>

                      {typeof value === 'object' ? (
                        <pre className="bg-white p-3 rounded-xl overflow-x-auto text-[11px] font-mono border border-border shadow-sm text-stone">
                          {displayValue}
                        </pre>
                      ) : isTechnicalField ? (
                        <div className="bg-charcoal/5 p-4 rounded-xl border border-charcoal/10">
                          <p className="whitespace-pre-wrap text-sm font-inter text-charcoal leading-relaxed">
                            {displayValue}
                          </p>
                        </div>
                      ) : isUrl ? (
                        <div className="flex items-center gap-3 bg-white p-2 rounded-xl border border-border shadow-sm">
                          {key.includes('image') ? (
                            <img src={value} alt="Preview" className="w-16 h-16 object-cover rounded-lg shadow-sm" />
                          ) : null}
                          <a
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline text-xs font-inter truncate flex-1"
                          >
                            {value}
                          </a>
                          <button
                            onClick={() => copyToClipboard(value, key)}
                            className="p-1.5 rounded-lg hover:bg-muted transition-colors text-stone hover:text-primary"
                            title={language === 'es' ? 'Copiar URL' : 'Copy URL'}
                          >
                            {copiedField === key ? (
                              <Check className="w-3.5 h-3.5" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      ) : (
                        <p className="px-1 text-sm font-inter text-stone leading-relaxed">
                          {displayValue}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDetailsDialogOpen(false)}
                className="rounded-full w-full sm:w-auto"
              >
                {t('close') || (language === 'es' ? 'Cerrar' : 'Close')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
